import { formatDuration, fromIsoDate, type HoursFormat, type IsoDate } from '@tt/shared';

export interface FormatContext {
  locale: string;
  timeZone: string;
  hoursFormat: HoursFormat;
}

export const formatMinutes = (minutes: number, ctx: FormatContext) =>
  formatDuration(minutes, ctx.hoursFormat);

/** "7.50 h" / "07:30" with a unit for screen readers where useful. */
export const formatHours = (minutes: number, ctx: FormatContext) =>
  ctx.hoursFormat === 'HH_MM'
    ? formatDuration(minutes, 'HH_MM')
    : `${formatDuration(minutes, 'DECIMAL')} h`;

export const formatDate = (
  date: IsoDate,
  ctx: FormatContext,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
) => new Intl.DateTimeFormat(ctx.locale, { timeZone: 'UTC', ...opts }).format(fromIsoDate(date));

export const formatWeekday = (
  date: IsoDate,
  ctx: FormatContext,
  style: 'short' | 'long' = 'short',
) =>
  new Intl.DateTimeFormat(ctx.locale, { timeZone: 'UTC', weekday: style }).format(
    fromIsoDate(date),
  );

export const formatDayNumber = (date: IsoDate, ctx: FormatContext) =>
  new Intl.DateTimeFormat(ctx.locale, { timeZone: 'UTC', day: 'numeric' }).format(
    fromIsoDate(date),
  );

export const formatMonth = (month: string, ctx: FormatContext) =>
  new Intl.DateTimeFormat(ctx.locale, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
    fromIsoDate(`${month}-01`),
  );

export const formatDateTime = (instant: string, ctx: FormatContext) =>
  new Intl.DateTimeFormat(ctx.locale, {
    timeZone: ctx.timeZone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(instant));

export const formatPercent = (value: number, ctx: FormatContext) =>
  new Intl.NumberFormat(ctx.locale, { style: 'percent', maximumFractionDigits: 0 }).format(value);

export const formatNumber = (value: number, ctx: FormatContext, digits = 2) =>
  new Intl.NumberFormat(ctx.locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value);
